/**
 * 前払い残高が尽きたときの案内（お金以外の画面用・2026-09-27 prepaid-onboarding）。純関数だけ（テストは prepaidGate.test.ts）。
 *
 * WHY: 無料枠なし（MASTER-PLAN §2）= 使えるお金が無い間（canSpend が false）は、プレイヤー側の処理（API・配信・マルチプレイ・Functions・AI）が止まり、
 * 新規作成・デプロイは 402 になる。止まっているのにプロジェクトの画面が普段どおりだと、クリエイターは「なぜ遊べないのか」が分からない。
 * なので概要・デプロイ（ホスティング）・新規作成の画面に帯を出し、入金の画面（/money/prepaid・Money の持ち物）へ 1 クリックで行けるようにする。
 * お金の画面の中は MoneyShell が自分で帯を出すので、ここは使わない。
 */

import type { PrepaidView } from './moneyApi.ts';

/**
 * GET /v1/money/prepaid のうち、ここで使う所だけ。型の正は moneyApi.ts の PrepaidView（Money の持ち物）から取る
 * （2026-09-27: 以前は自前の型で canSpend を書いていたが、Money が PrepaidView に canSpend を足したので 1 か所に寄せた）。
 * canSpend = サーバーの developerCanSpend そのもの（①前払い残高 − 請求残 > 0 ②売上からの自動充当 ON で出金できる売上がある
 * ③自動チャージ ON・カード保存済み・最後の自動チャージが失敗していない のどれか）。false なら新規作成・デプロイも 402。古いサーバーは undefined
 * invite / balanceStatus（2026-09-29 creator/launch-close-2・Money の前払いの道 16）: どちらも古い本体には無い = 省略可のまま読む
 */
export type PrepaidLike = Pick<PrepaidView, 'balance' | 'owed' | 'canSpend' | 'invite' | 'balanceStatus'>;

/**
 * 帯の種類。null = 出さない。
 *   'invitePending' … 招待コードを押さえたがメールの確認待ち（invite.pending）。入金ではなく「確認すると {granted} が入ります」
 *                     WHY 入金の帯を出さないか: 確認前は入金も 403 email_unverified（Core SEC-DEVLOGIN-01）。しかも確認すれば $5 が入って
 *                     そのまま使える = 「まず入金」は二重に誤り（Money の前払いの道の一覧 16・17）
 *   'start' … 残高 0・請求残 0 で、招待クレジットも無い（まだ入金していない新しい人）→ 「最初に入金」
 *   'owed'  … 請求残がある（使った分が残高を超えた）→ 「止まっている・入金で自動再開」
 *   'empty' … それ以外で止まっている（残高と請求残が相殺して 0 以下・**招待クレジットを使い切った人もここ** = 「最初に入金」ではなく「使い切った」）
 *   'low' / 'lowInvite' … 止まってはいないが、サーバーの balanceStatus が 'low'（lowInvite = 残りのほとんどが招待クレジット）。
 *                     WHY お金以外の画面にも出すか: 止まってから気付くと利用者に対して止まる。少ない段階で入金へ 1 クリックで行けるように（一覧 16）
 * 止まっている帯（start / owed / empty）は **canSpend が false のときだけ**（止まっていないのに「止まっている」と言わない）。
 *
 * 経緯: 以前（2026-09-27 prepaid-onboarding）は canSpend が API に無く、③ だけを autoTopUp から真似て ② は見られなかった
 *   = 売上で回っている人にも帯が出うるので「売上や自動チャージで埋まらない限り」とぼかしていた。Money が canSpend を足したので、判定はサーバーに任せて言い切る。
 * canSpend が無い（古いサーバー）ときは止まっている帯を出さない: 推測で「止まっている」と言うより、止まっていれば 402 のエラー側の案内に任せる方が誤りが無い。
 *   balanceStatus が無い（古いサーバー）ときは low の帯も出さない（同じ理由。残高と線から画面で推測しない = 招待の分の線はサーバーだけが知っている）
 */
export type PrepaidBandKind = 'invitePending' | 'start' | 'owed' | 'empty' | 'low' | 'lowInvite';
export function prepaidBand(p: PrepaidLike | null | undefined): PrepaidBandKind | null {
  if (!p) return null;
  // 確認待ちは canSpend を見ない: 確認前に入金はできない（403）ので、ほかの財源で動いていることは無い（念のため canSpend true なら出さない）
  if (p.invite?.pending === true && p.canSpend !== true) return 'invitePending';
  if (p.canSpend === false) {
    if (p.owed > 0) return 'owed';
    // 招待クレジットが入ったことがある（granted > 0・確認済み）人の 0 は「使い切った」= 'empty'（「最初に入金」「$5 から」と言わない）
    return notFundedYet(p) ? 'start' : 'empty';
  }
  if (p.balanceStatus === 'low') return inviteMostly(p) ? 'lowInvite' : 'low';
  return null;
}

/**
 * 'start'（まだお金が入っていない新しい人）と 'empty'（使い切った人）の見分け。止まっている（canSpend false・請求残 0）前提で呼ぶ。
 * WHY 切り出したか（2026-10-29 core/open-signup-polish）: 上のバーの残高（usageView.ts balanceChip）も同じ見分けで
 *   「Ran out」と「Add balance」を出し分ける。招待なしで登録した人（10/31 から全員）は $0 で始まるので、赤の「RAN OUT」は事実と違う。
 *   帯とバーで別の式を持つと食い違うので 1 か所に置く。
 * 限界: 入金した額をちょうど 0 まで使い切った人も balance 0・invite null で 'start' に見える（API に「入金したことがある」が無い）。
 *   その人に「入金する」と言っても誤りではない（次にすることは同じ）ので、赤で「使い切った」と言うより穏やかな方に倒す
 */
export function notFundedYet(p: { balance: number; invite?: { granted: number } | null }): boolean {
  return p.balance === 0 && !(p.invite && p.invite.granted > 0);
}

/**
 * 残高のほとんどが招待クレジットか（サーバーの lowBalance.ts effectiveLowLine の inviteMostly と同じ式: 招待の残りが残高の半分以上）。
 * 文言だけに使う（「招待クレジットの残りがわずか」か「残高が少ない」か）。判定の正はサーバー（notices の detail.invite も同じ式）
 */
export function inviteMostly(p: { balance: number; invite?: { remaining: number } | null }): boolean {
  const r = p.invite?.remaining ?? 0;
  return r > 0 && r * 2 >= p.balance;
}

/**
 * 402 のうち「前払い残高が足りない」もの（CLI の lib/api.ts isPrepaid402 と同じ規則）。
 * いまのコード: balance_due（新規作成・デプロイ・Functions。Functions は 2026-09-27 Core の E2E-07 で prepaid_required から変更）・
 * prepaid_required（旧サーバーの Functions）・insufficient_credits（AI）。
 * Core / Money が「0 以下で止める」を入れるときに新しいコードが増えうるので、402 は x_signin_unavailable（プレイヤー向け）以外すべて
 */
export function isPrepaid402(err: { status?: number; code?: string } | null | undefined): boolean {
  return !!err && err.status === 402 && err.code !== 'x_signin_unavailable';
}
